// frontend/src/widgets/ResourceFormWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-04
// Stage 2A:   2026-10-26 GMT — Restore form extension: vendor company selector and payload.
// Updated:      2026-10-26 GMT
// Stage 2B: 2026-10-28 GMT — vendor contact linkage; full existing functionality retained.
//
// Description:
//   Restore the Create Resource form accidentally replaced by the Expenses widget.
//   Resource creation uses the authenticated server-owned tenant; no frontend
//   tenant header or tenant_id is supplied. One POST creates the resource,
//   followed only by optional avatar upload. Permanent tenant-scoped cost centre
//   and subtype IDs replace the obsolete temporary labels in notes.
//   Retains original location map, roles, avatar, resource types and fields.
//
// Change Log (GMT/BST):
// 2026-10-26  Restore from original 2025 Git version (2ce47fa).
//             Remove client tenant derivation and X-Tenant-ID mutations.
//             Use persisted cost-centre IDs and checked taxonomy lookups.
//             Preserve the existing Expenses widget independently.
//
// Dependencies:
//   POST /resources/ (server resolves tenant_id, ResourceCreate schema optional)
//   GET /cost-centers/ and GET /cost-centers/{id}/subtypes
//   GET /roles/, GET /geocode, POST /resources/{id}/avatar
//
import React, { useEffect, useRef, useState } from "react";
import api from "../api/axiosInstance";
import VendorResourceContactLink from "../components/VendorResourceContactLink";
import L from "leaflet";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const inputStyle = { height: 34, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, width: "100%" };
const selectStyle = { ...inputStyle };
const textAreaStyle = { ...inputStyle, height: 90, resize: "vertical" };

function useThemedBtn() {
  const [hover, setHover] = useState(false);
  const [down, setDown] = useState(false);
  return {
    hover, down,
    bind: {
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => { setHover(false); setDown(false); },
      onMouseDown: () => setDown(true),
      onMouseUp: () => setDown(false),
    },
  };
}
const btnBase = { padding: "8px 12px", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, color: "#fff" };
const actionBg = (hover, down) =>
  down ? "var(--btn-action-click, #d95d00)" :
  hover ? "var(--btn-action-hover, #ff8f33)" :
          "var(--btn-action-normal, #ff7a00)";

const TIMEZONES = [
  "Europe/London","Europe/Berlin","Europe/Paris","UTC",
  "America/New_York","America/Los_Angeles","America/Chicago","America/Denver",
  "Asia/Kolkata","Asia/Tokyo","Asia/Singapore","Australia/Sydney",
];

// ---------- helpers ----------
const nOrOmit = (v) => {
  if (v === "" || v === null || typeof v === "undefined") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const boolOrOmit = (v) => (typeof v === "boolean" ? v : undefined);
const strOrOmit = (v) => {
  const s = (v ?? "").toString().trim();
  return s ? s : undefined;
};
const idOrOmit = (v) => {
  if (v === "" || v === null || typeof v === "undefined") return undefined;
  const n = Number(v);
  return Number.isInteger(n) ? n : undefined;
};

const ResourceFormWidget = ({ locations = [], onCreated }) => {
  // ---- Permanent tenant-scoped taxonomy; no tenant id is accepted from the UI.
  const [costCenters, setCostCenters] = useState([]);
  const [costSubtypes, setCostSubtypes] = useState([]);
  const [taxLoading, setTaxLoading] = useState(true);
  const [subLoading, setSubLoading] = useState(false);
  const [taxError, setTaxError] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/cost-centers/", { params: { limit: 500 } })
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data?.items;
        if (!Array.isArray(rows)) throw new Error("Invalid cost-centre response");
        if (alive) { setCostCenters(rows); setTaxError(""); }
      })
      .catch((err) => { if (alive) setTaxError(err?.response?.data?.detail || err?.message || "Cost centres unavailable"); })
      .finally(() => { if (alive) setTaxLoading(false); });
    return () => { alive = false; };
  }, []);

  // ---- Roles (trailing slash)
  const [roles, setRoles] = useState([]);
  const [rolesErr, setRolesErr] = useState("");
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get("/roles/");
        const arr = Array.isArray(res.data) ? res.data : res.data?.items || [];
        if (active) setRoles(arr);
      } catch (e) {
        setRolesErr(e?.response?.data?.detail || e?.message || "Roles not available");
        setRoles([]);
      }
    })();
    return () => { active = false; };
  }, []);

  // Tenant-scoped company options. Never supply a tenant ID from the browser.
  const [vendorCompanies, setVendorCompanies] = useState([]);
  const [vendorCompaniesError, setVendorCompaniesError] = useState("");
  useEffect(() => {
    let alive = true;
    api.get("/customers/")
      .then(({ data }) => {
        if (!alive) return;
        const rows = Array.isArray(data) ? data : data?.items;
        if (!Array.isArray(rows)) throw new Error("Invalid customer response");
        setVendorCompanies(rows.filter((c) => c.is_vendor === true));
        setVendorCompaniesError("");
      })
      .catch((err) => {
        if (alive) setVendorCompaniesError(err?.response?.data?.detail || "Unable to load vendor companies");
      });
    return () => { alive = false; };
  }, []);

  // ---- Form state
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    job_title: "",
    resource_type: "employee",
    vendor_customer_id: "",
    employment_status: "active",
    active: true,
    is_billable: true,

    capacity_hours_per_week: "",
    timezone: "Europe/London",
    home_postcode: "",
    home_lat: 51.5074,
    home_lon: -0.1278,

    location_id: "",
    identifier: "",
    system_role: "",

    hire_date: "",
    leave_date: "",

    cost_rate: "",
    default_bill_rate: "",
    burden_cost: "",

    notes: "",

    cost_center_id: "",
    cost_center_subtype_id: "",

    avatar_file: null,
    avatar_preview: "",
  });
  useEffect(() => {
    const categoryId = idOrOmit(form.cost_center_id);
    if (!categoryId) { setCostSubtypes([]); setSubLoading(false); return; }
    let alive = true;
    setSubLoading(true);
    api.get(`/cost-centers/${categoryId}/subtypes`)
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data?.items;
        if (!Array.isArray(rows)) throw new Error("Invalid cost-centre subtype response");
        if (alive) { setCostSubtypes(rows); setTaxError(""); }
      })
      .catch((err) => { if (alive) setTaxError(err?.response?.data?.detail || err?.message || "Cost-centre subtypes unavailable"); })
      .finally(() => { if (alive) setSubLoading(false); });
    return () => { alive = false; };
  }, [form.cost_center_id]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const [saving, setSaving] = useState(false);
  const canSave = !!form.first_name && !!form.last_name && !!form.email;
  const createBtn = useThemedBtn();

  // ---- Map
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);

  const centerOn = (lat, lon, z = 13) => {
    const map = mapObjRef.current, marker = markerRef.current;
    if (!map || !marker) return;
    const target = L.latLng(lat, lon);
    marker.setLatLng(target);
    map.setView(target, z, { animate: true });
  };

  useEffect(() => {
    if (mapObjRef.current || !mapRef.current) return;
    const map = L.map(mapRef.current);
    map.setView([form.home_lat, form.home_lon], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map);
    const marker = L.marker([form.home_lat, form.home_lon], { draggable: true }).addTo(map);
    marker.on("dragend", () => {
      const p = marker.getLatLng();
      setField("home_lat", Number(p.lat.toFixed(6)));
      setField("home_lon", Number(p.lng.toFixed(6)));
    });
    map.on("click", (e) => {
      marker.setLatLng(e.latlng);
      setField("home_lat", Number(e.latlng.lat.toFixed(6)));
      setField("home_lon", Number(e.latlng.lng.toFixed(6)));
    });
    mapObjRef.current = map;
    markerRef.current = marker;
    return () => { map.off(); map.remove(); mapObjRef.current = null; markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Geocode
  const geocodeAbortRef = useRef(null);
  const geocodePostcode = async (postcode) => {
    if (!postcode || !postcode.trim()) return null;
    if (geocodeAbortRef.current) geocodeAbortRef.current.abort();
    const ctrl = new AbortController();
    geocodeAbortRef.current = ctrl;
    try {
      const r = await api.get("/geocode", { params: { postcode }, signal: ctrl.signal });
      const { lat, lon } =
        r?.data?.lat && r?.data?.lon
          ? r.data
          : Array.isArray(r?.data) && r.data.length
          ? { lat: r.data[0].lat, lon: r.data[0].lon }
          : {};
      if (lat && lon) return { lat: Number(lat), lon: Number(lon) };
    } catch {}
    try {
      const resp = await fetch(
        "https://nominatim.openstreetmap.org/search?format=json&limit=1&q="+encodeURIComponent(postcode),
        { signal: ctrl.signal, headers: { "Accept-Language": "en-GB" } }
      );
      const arr = await resp.json();
      if (Array.isArray(arr) && arr.length) {
        return { lat: Number(arr[0].lat), lon: Number(arr[0].lon) };
      }
    } catch {}
    return null;
  };

  useEffect(() => {
    if (!form.home_postcode) return;
    const deb = setTimeout(async () => {
      const out = await geocodePostcode(form.home_postcode);
      if (out?.lat && out?.lon) {
        setField("home_lat", Number(out.lat.toFixed(6)));
        setField("home_lon", Number(out.lon.toFixed(6)));
        centerOn(out.lat, out.lon, 13);
      }
    }, 500);
    return () => clearTimeout(deb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.home_postcode]);

  // ---- Avatar preview (persisted after create via upload)
  const onPickAvatar = (file) => {
    if (!file) {
      setField("avatar_file", null);
      setField("avatar_preview", "");
      return;
    }
    setField("avatar_file", file);
    const reader = new FileReader();
    reader.onload = (e) => setField("avatar_preview", e.target.result);
    reader.readAsDataURL(file);
  };

  // ---- Create (single POST)
  const [createCompanyContact, setCreateCompanyContact] = useState(true);

  const handleCreate = async () => {
    if (!canSave || saving) return;
    if (taxLoading || subLoading || taxError) {
      alert(taxError || "Cost-centre taxonomy is not ready.");
      return;
    }
    const categoryId = idOrOmit(form.cost_center_id);
    const subtypeId = idOrOmit(form.cost_center_subtype_id);
    if (categoryId && !costCenters.some((c) => Number(c.id) === categoryId)) {
      alert("Invalid cost-centre category."); return;
    }
    if (subtypeId && (!categoryId || !costSubtypes.some((c) => Number(c.id) === subtypeId && Number(c.cost_center_id) === categoryId))) {
      alert("Invalid cost-centre subtype."); return;
    }
    setSaving(true);
    try {
      const payload = {
        // Required identity; the API derives tenant_id from get_current_user().
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,

        // booleans
        active: boolOrOmit(form.active),
        is_billable: boolOrOmit(form.is_billable),

        // simple strings
        mobile: strOrOmit(form.mobile),
        job_title: strOrOmit(form.job_title),
        resource_type: strOrOmit(form.resource_type) || "employee",
        vendor_customer_id: idOrOmit(form.vendor_customer_id) ?? null,
        employment_status: strOrOmit(form.employment_status) || "active",
        timezone: strOrOmit(form.timezone) || "Europe/London",
        home_postcode: strOrOmit(form.home_postcode),
        identifier: strOrOmit(form.identifier),
        system_role: strOrOmit(form.system_role),

        // dates
        hire_date: strOrOmit(form.hire_date),
        leave_date: strOrOmit(form.leave_date),

        // numerics
        capacity_hours_per_week: nOrOmit(form.capacity_hours_per_week),
        cost_rate: nOrOmit(form.cost_rate),
        default_bill_rate: nOrOmit(form.default_bill_rate),
        burden_cost: nOrOmit(form.burden_cost),
        home_lat: nOrOmit(form.home_lat),
        home_lon: nOrOmit(form.home_lon),

        // foreign keys
        location_id: idOrOmit(form.location_id),

        // Permanent taxonomy IDs and unmodified notes
        cost_center_id: categoryId,
        cost_center_subtype_id: subtypeId,
        notes: strOrOmit(form.notes),
      };

      // Strip undefined keys (serializer hates undefined)
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      // SINGLE create call — with trailing slash
      const createRes = await api.post("/resources/", payload);
      const newId = createRes?.data?.id;

      // Stage 2B: explicit option selected prior to save; resource ID is server-owned.
      let companyContactError = "";
      if (newId && createCompanyContact && ["vendor", "contractor"].includes(form.resource_type) && form.vendor_customer_id) {
        try { await api.post(`/resources/${newId}/vendor-contact`, {}); }
        catch (err) { companyContactError = err?.response?.data?.detail || "Unable to create company contact"; }
      }

      // Avatar upload (optional, trailing slash + "file")
      if (newId && form.avatar_file) {
        const fd = new FormData();
        fd.append("file", form.avatar_file);
        try {
          await api.post(`/resources/${newId}/avatar/`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          console.warn("⚠️ Avatar upload failed:", err?.response?.data || err?.message || err);
        }
      }

      // Reset form + notify
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        job_title: "",
        resource_type: "employee",
    vendor_customer_id: "",
        employment_status: "active",
        active: true,
        is_billable: true,

        capacity_hours_per_week: "",
        timezone: "Europe/London",
        home_postcode: "",
        home_lat: 51.5074,
        home_lon: -0.1278,

        location_id: "",
        identifier: "",
        system_role: "",

        hire_date: "",
        leave_date: "",

        cost_rate: "",
        default_bill_rate: "",
        burden_cost: "",

        notes: "",
        cost_center_id: "",
        cost_center_subtype_id: "",

        avatar_file: null,
        avatar_preview: "",
      });

      onCreated && onCreated();
      alert(companyContactError ? `Resource created, but company contact was not linked: ${companyContactError}` : "Resource created.");
    } catch (e) {
      const server = e?.response?.data;
      const detail =
        typeof server === "string" ? server :
        server?.detail ? server.detail :
        server || e?.message || "Unknown error";
      console.error("Create resource failed:", server || e);
      alert(`Create failed:\n${(typeof detail === "object" ? JSON.stringify(detail, null, 2) : String(detail))}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="bg-accent text-white px-4 py-2 rounded-md mb-2 text-sm font-semibold">
        Create Resource
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label>First Name</label>
          <input style={inputStyle} value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} />
        </div>
        <div>
          <label>Last Name</label>
          <input style={inputStyle} value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} />
        </div>

        <div>
          <label>Email</label>
          <input style={inputStyle} value={form.email} onChange={(e) => setField("email", e.target.value)} />
        </div>
        <div>
          <label>Mobile</label>
          <input style={inputStyle} value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} />
        </div>

        <div>
          <label>Resource Type</label>
          <select style={selectStyle} value={form.resource_type} onChange={(e) => setField("resource_type", e.target.value)}>
            <option value="employee">Employee</option>
            <option value="contractor">Contractor</option>
            <option value="vendor">Vendor</option>
          </select>
        </div>
        {(["vendor", "contractor"].includes(form.resource_type)) && (
          <div>
            <label>Vendor company (optional)</label>
            <select style={selectStyle} value={form.vendor_customer_id} disabled={!!vendorCompaniesError} onChange={(e) => setField("vendor_customer_id", e.target.value)}>
              <option value="">No vendor company</option>
              {vendorCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {vendorCompaniesError && <small role="alert">{vendorCompaniesError}</small>}
          </div>
        )}
        <VendorResourceContactLink
          enabled={["vendor", "contractor"].includes(form.resource_type) && !!form.vendor_customer_id}
          onPreferenceChange={setCreateCompanyContact}
        />
        <div>
          <label>Employment Status</label>
          <select style={selectStyle} value={form.employment_status} onChange={(e) => setField("employment_status", e.target.value)}>
            <option value="active">Active</option>
            <option value="on_leave">On Leave</option>
            <option value="terminated">Terminated</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        <div>
          <label>Active</label>
          <input type="checkbox" checked={!!form.active} onChange={(e) => setField("active", e.target.checked)} />
        </div>
        <div>
          <label>Billable</label>
          <input type="checkbox" checked={!!form.is_billable} onChange={(e) => setField("is_billable", e.target.checked)} />
        </div>

        <div>
          <label>Job Title</label>
          <input style={inputStyle} value={form.job_title} onChange={(e) => setField("job_title", e.target.value)} />
        </div>
        <div>
          <label>Timezone</label>
          <select style={selectStyle} value={form.timezone} onChange={(e) => setField("timezone", e.target.value)}>
            {TIMEZONES.map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
          </select>
        </div>

        <div>
          <label>Capacity (hrs/week)</label>
          <input type="number" style={inputStyle} value={form.capacity_hours_per_week} onChange={(e) => setField("capacity_hours_per_week", e.target.value)} />
        </div>
        <div>
          <label>Home Postcode</label>
          <input style={inputStyle} value={form.home_postcode} onChange={(e) => setField("home_postcode", e.target.value)} />
        </div>

        <div>
          <label>Identifier</label>
          <input style={inputStyle} value={form.identifier} onChange={(e) => setField("identifier", e.target.value)} placeholder="e.g., RES-000123" />
        </div>

        {/* System Role — lookup with fallback */}
        <div>
          <label>System Role</label>
          {roles.length > 0 ? (
            <select style={selectStyle} value={form.system_role} onChange={(e) => setField("system_role", e.target.value)}>
              <option value="">(none)</option>
              {roles.map((r) => (
                <option key={r.id ?? r.name} value={r.name}>{r.name}</option>
              ))}
            </select>
          ) : (
            <input
              style={inputStyle}
              value={form.system_role}
              onChange={(e) => setField("system_role", e.target.value)}
              placeholder={rolesErr ? `Fallback — ${rolesErr}` : "(type a role)"}
            />
          )}
        </div>

        <div>
          <label>Hire Date</label>
          <input type="date" style={inputStyle} value={form.hire_date} onChange={(e) => setField("hire_date", e.target.value)} />
        </div>
        <div>
          <label>Leave Date</label>
          <input type="date" style={inputStyle} value={form.leave_date} onChange={(e) => setField("leave_date", e.target.value)} />
        </div>

        <div>
          <label>Cost Rate</label>
          <input type="number" step="0.01" style={inputStyle} value={form.cost_rate} onChange={(e) => setField("cost_rate", e.target.value)} />
        </div>
        <div>
          <label>Default Bill Rate</label>
          <input type="number" step="0.01" style={inputStyle} value={form.default_bill_rate} onChange={(e) => setField("default_bill_rate", e.target.value)} />
        </div>
        <div>
          <label>Burden Cost</label>
          <input type="number" step="0.01" style={inputStyle} value={form.burden_cost} onChange={(e) => setField("burden_cost", e.target.value)} />
        </div>

        {/* Persisted tenant-scoped cost centre taxonomy */}
        <div>
          <label>Cost Centre</label>
          <select
            style={selectStyle}
            value={form.cost_center_id}
            disabled={taxLoading || saving || !!taxError}
            onChange={(e) => setForm((p) => ({ ...p, cost_center_id: e.target.value, cost_center_subtype_id: "" }))}
          >
            <option value="">(none)</option>
            {costCenters.filter((c) => c.active).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Cost Centre Subtype</label>
          <select
            style={selectStyle}
            value={form.cost_center_subtype_id}
            disabled={!form.cost_center_id || subLoading || saving || !!taxError}
            onChange={(e) => setField("cost_center_subtype_id", e.target.value)}
          >
            <option value="">(none)</option>
            {costSubtypes.filter((c) => c.active).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {taxError && <div style={{ gridColumn: "1 / -1", color: "#a33" }}>Cost-centre taxonomy unavailable: {String(taxError)}</div>}

        {/* Avatar */}
        <div style={{ gridColumn: "1 / span 2" }}>
          <label>Avatar (optional)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <input type="file" accept="image/*" style={{ ...inputStyle, height: 45, padding: "8px 10px" }} onChange={(e) => onPickAvatar(e.target.files?.[0])} />
            {form.avatar_preview ? (
              <img src={form.avatar_preview} alt="avatar preview" style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", border: "1px solid #ddd" }} />
            ) : (
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f2f2f2", border: "1px solid #ddd",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#777" }}>No preview</div>
            )}
          </div>
        </div>

        {/* Map + Lat/Lon */}
        <div style={{ gridColumn: "1 / span 2" }}>
          <label>Home Location</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
            <div>
              <label>Latitude</label>
              <input type="number" step="0.000001" style={inputStyle} value={form.home_lat} onChange={(e) => setField("home_lat", e.target.value)} />
            </div>
            <div>
              <label>Longitude</label>
              <input type="number" step="0.000001" style={inputStyle} value={form.home_lon} onChange={(e) => setField("home_lon", e.target.value)} />
            </div>
          </div>
          <div ref={mapRef} style={{ height: 280, borderRadius: 10, overflow: "hidden", border: "1px solid #e5e5e5" }} />
          <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
            Click the map or drag the pin to refine location.
          </div>
        </div>

        {/* Notes */}
        <div style={{ gridColumn: "1 / span 2" }}>
          <label>Notes</label>
          <textarea style={textAreaStyle} value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="Additional details…" />
        </div>
      </div>

      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
        <button
          {...createBtn.bind}
          disabled={!canSave || saving || taxLoading || subLoading || !!taxError}
          onClick={handleCreate}
          style={{
            ...btnBase,
            background: actionBg(createBtn.hover, createBtn.down),
            opacity: (!canSave || saving || taxLoading || subLoading || !!taxError) ? 0.8 : 1,
          }}
        >
          {saving ? "Creating..." : "Create Resource"}
        </button>
      </div>
    </div>
  );
};

export default ResourceFormWidget;
